// The §2.12 danger confirm in its Modal container (UI-LAW §4a — the blocking
// alternative to the inline RowConfirm, for table rows where an in-cell reveal
// would shift the row): a dialog naming the exact target — sentence, optional
// inline mutation error, Cancel then the danger confirm (danger always last).
// Fully controlled: the caller mounts it only while a target is selected, owns
// the mutation, and keeps it mounted on failure so the error surfaces inline.
// Drawer-contained confirms stay on RowConfirm; archive-grade actions add the
// name-match TextInput pattern.

import { Button, Inline, InlineError, Modal, Stack, Text } from "../../ui";

export interface ConfirmModalProps {
  // Accessible dialog name, e.g. "Remove member?".
  ariaLabel: string;
  // Cancel button label. Default "Cancel".
  cancelLabel?: string;
  // Danger confirm label, e.g. "Remove member".
  confirmLabel: string;
  // This attempt's mutation error, rendered inline above the actions.
  errorMessage?: string | null;
  // True while the destructive mutation is in flight; disables both buttons
  // and swaps the confirm label for `pendingLabel`.
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  // In-flight confirm label, e.g. "Removing...".
  pendingLabel: string;
  // The §2.12 sentence naming the exact target, e.g.
  // `Remove Casey Cook from Support team? This removes the membership.`
  sentence: string;
}

export function ConfirmModal({
  ariaLabel,
  cancelLabel = "Cancel",
  confirmLabel,
  errorMessage,
  isPending,
  onCancel,
  onConfirm,
  pendingLabel,
  sentence,
}: ConfirmModalProps) {
  return (
    <Modal ariaLabel={ariaLabel} isOpen onDismiss={onCancel}>
      <Stack gap={4}>
        <Text>{sentence}</Text>
        {errorMessage ? <InlineError>{errorMessage}</InlineError> : null}
        <Inline gap={2}>
          <Button disabled={isPending} type="button" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            disabled={isPending}
            type="button"
            variant="danger"
            onClick={onConfirm}
          >
            {isPending ? pendingLabel : confirmLabel}
          </Button>
        </Inline>
      </Stack>
    </Modal>
  );
}
